import { useEffect, useRef } from 'react';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';

type UseSyncRegistryCurrencyToFormArgs = Readonly<{
  isEditDialog?: boolean;
  isNewCashFund?: boolean;
  registryCurrencyCode: Maybe<string>;
  formCurrencyCode: Maybe<string>;
  onCurrencyChange: (currencySymbol: string, currencyCode: string) => void;
}>;

/**
 * Syncs the registry currency into the cash fund form whenever the two diverge
 * (e.g. after Stripe payout onboarding, or the "Use a different currency" flow, sets
 * a non-USD currency for a new fund).
 *
 * Runs for new funds — custom adds (`isEditDialog === false`) and catalog adds
 * (`isEditDialog === true` + `isNewCashFund === true`) — but bails for existing-fund
 * edits, whose currency is locked.
 *
 * The form's own `currencyCode` is the baseline, so the update is applied surgically via
 * `onCurrencyChange` and re-applies itself if formik reverts the field — `resetForm()`, or
 * an `enableReinitialize` pass — instead of going stale against an internal snapshot. The
 * hook must not write to the ref backing the form's derived `initialValues`: that changes
 * `initialValues` and, with `enableReinitialize`, resets the whole form, wiping the host's
 * other input.
 */
export const useSyncRegistryCurrencyToForm = ({ isEditDialog, isNewCashFund, registryCurrencyCode, formCurrencyCode, onCurrencyChange }: UseSyncRegistryCurrencyToFormArgs) => {
  const onCurrencyChangeRef = useRef(onCurrencyChange);
  onCurrencyChangeRef.current = onCurrencyChange;

  useEffect(() => {
    if (isEditDialog && !isNewCashFund) return;
    if (!registryCurrencyCode || registryCurrencyCode === formCurrencyCode) return;
    onCurrencyChangeRef.current(getCurrencySymbolFromCurrencyCode(registryCurrencyCode)!, registryCurrencyCode);
  }, [registryCurrencyCode, formCurrencyCode, isEditDialog, isNewCashFund]);
};
